import { type AnalyticsPeriod, type CustomDateRange } from "@/components/analytics-period-selector"

export interface PeriodRange {
  startDate: Date
  endDate: Date
  startTimestamp: number
  endTimestamp: number
}

export interface DayPeriod {
  year: number
  month: number
  day: number
}

/**
 * Получить диапазон дат для заданного периода
 */
export function getPeriodRange(period: AnalyticsPeriod, customRange?: CustomDateRange): PeriodRange {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  
  let startDate: Date
  let endDate: Date

  switch (period) {
    case 'today':
      // Только сегодня: с начала дня до конца дня (но не включая следующий день)
      startDate = new Date(today)
      endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      break
      
    case 'yesterday':
      // Только вчера: с начала вчера до конца вчера
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)
      startDate = new Date(yesterday)
      endDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999)
      break
      
    case 'week':
      // Последние 7 дней включая сегодня
      startDate = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)
      endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      break
      
    case 'month':
      // Последние 30 дней включая сегодня (заканчиваем концом сегодня, НЕ завтра!)
      startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000)
      endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      break
      
    case 'quarter':
      // Последние 90 дней включая сегодня
      startDate = new Date(today.getTime() - 89 * 24 * 60 * 60 * 1000)
      endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      break
      
    case 'custom':
      // Кастомный период из календаря
      if (!customRange) {
        // Если не задан кастомный диапазон, используем последний месяц по умолчанию
        startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000)
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
      } else {
        startDate = new Date(customRange.from)
        startDate.setHours(0, 0, 0, 0)
        endDate = new Date(customRange.to)
        endDate.setHours(23, 59, 59, 999)
      }
      break
  }

  return {
    startDate,
    endDate,
    startTimestamp: Math.floor(startDate.getTime() / 1000),
    endTimestamp: Math.floor(endDate.getTime() / 1000)
  }
}

/**
 * Получить предыдущий период той же длительности
 */
export function getPreviousPeriodRange(period: AnalyticsPeriod, customRange?: CustomDateRange): PeriodRange {
  const currentRange = getPeriodRange(period, customRange)
  const duration = currentRange.endTimestamp - currentRange.startTimestamp
  
  const prevEndTimestamp = currentRange.startTimestamp - 1
  const prevStartTimestamp = prevEndTimestamp - duration
  
  return {
    startDate: new Date(prevStartTimestamp * 1000),
    endDate: new Date(prevEndTimestamp * 1000),
    startTimestamp: prevStartTimestamp,
    endTimestamp: prevEndTimestamp
  }
}

/**
 * Получить параметры дня для DAU запросов
 */
export function getDayParameters(period: AnalyticsPeriod, customRange?: CustomDateRange): DayPeriod | null {
  if (period !== 'today' && period !== 'yesterday') {
    return null // DAU применимо только для конкретных дней
  }
  
  const range = getPeriodRange(period, customRange)
  const date = range.startDate
  
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1, // API ожидает месяц с 1
    day: date.getDate()
  }
}

/**
 * Получить параметры предыдущего дня
 */
export function getPreviousDayParameters(period: AnalyticsPeriod, customRange?: CustomDateRange): DayPeriod | null {
  if (period !== 'today' && period !== 'yesterday') {
    return null
  }
  
  const range = getPeriodRange(period, customRange)
  const prevDay = new Date(range.startDate.getTime() - 24 * 60 * 60 * 1000)
  
  return {
    year: prevDay.getFullYear(),
    month: prevDay.getMonth() + 1,
    day: prevDay.getDate()
  }
}

/**
 * Получить человекочитаемое описание периода
 */
export function getPeriodDescription(period: AnalyticsPeriod, customRange?: CustomDateRange): string {
  if (period === 'custom' && customRange) {
    const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
    const fromStr = `${customRange.from.getDate()} ${months[customRange.from.getMonth()]}`
    const toStr = `${customRange.to.getDate()} ${months[customRange.to.getMonth()]}`
    return `${fromStr} - ${toStr}`
  }
  
  const descriptions: Record<Exclude<AnalyticsPeriod, 'custom'>, string> = {
    today: 'сегодня',
    yesterday: 'вчера',
    week: 'за неделю',
    month: 'за месяц', 
    quarter: 'за квартал'
  }
  
  return descriptions[period as Exclude<AnalyticsPeriod, 'custom'>] || 'выбранный период'
}
